import { apiRequest, appendScopeToApiUrl } from '@/lib/queryClient';
import {
  PaginationParams,
  PaginatedPlansResponse,
  CreatePlanInput,
  CreateObjectiveInput,
  ObjectiveBatchSaveInput,
  ObjectiveBatchSaveResult,
  UpdateObjectiveInput,
  FocusRequirementWithDetails,
  Activity,
  ActivityBatchSaveInput,
  ActivityBatchSaveResult,
  CreateActivityInput,
  CreatePlanDefinitionApprovalInput,
  CreateFocusDefinitionApprovalInput,
  CreateFocusFulfillmentInput,
  CreatePlanValidationInput,
  DEFAULT_SORT_BY,
  Objective,
  PlanRequirementDecision,
  PlanRequirementDecisionInput,
  FocusName,
  PowerBiReportImageResponse,
  SelfAssessmentResultsPayload,
  AnnualPlanComplianceContext,
  AnnualPlanStep0Context,
  AnnualPlanStep1Context,
  AnnualPlanStep2Context,
  ChecklistResponseBatchInput,
  ChecklistResponsesBatchSaveResult,
  AnnualPlanExportFilters,
} from '@shared/schema';
import { SaveChecklistResponseDTO, SaveChecklistResponseResult } from '../types/state';
import { buildAnnualPlansExportQuery } from './annualPlansExport';

export default function DataSourceService({ baseUrl }: { baseUrl: string }) {
  const PATH_PLANS_PAGINATED = 'api/annual-plans/paginated';
  const PATH_V2_PLANS = 'api/v2/annual-plans';
  const PATH_PLANS = 'api/annual-plans';
  const PATH_V2_VERSIONS = 'api/v2/annual-plan-versions';
  const PATH_FOCUSES = 'api/focuses';
  const PATH_OBJECTIVES = 'api/objectives';
  const PATH_ACTIVITIES = 'api/activities';
  const PATH_REQUIREMENTS = 'api/requirements';
  const PATH_EXTERNAL = 'api/external-data';
  const PATH_ANNUAL_PLANS_EXPORT = 'api/annual-plans/export';
  const PATH_COOKIE_REPORT = 'test-api/cookie-report';
  const PATH_CHECKLISTS = 'api/checklists';
  const PATH_FULFILLMENT_PROGRESS = 'api/fulfillment-progress';
  const PATH_ACCIDENT_ANALYSIS = 'api/accident-analysis';
  const PATH_SELF_ASSESSMENT_RESULTS = 'api/annual-plans/fulfillment-versions';

  return {
    getPaginatedPlans,
    getAnnualPlanById,
    createAnnualPlan,
    getAPVersions,
    createAnnualPlanVersion,
    fetchAllFocuses,
    getFocusNamesByIds,
    fetchFocusesFulfillmentProgress,
    fetchObjectivesByVersionId,
    createObjective,
    saveObjectivesBatch,
    updateObjective,
    deleteObjective,
    getActivitiesByObjectiveId,
    createActivity,
    saveActivitiesBatch,
    updateActivityDefinition,
    deleteActivity,
    updateActivityProgress,
    uploadActivityEvidence,
    deleteActivityEvidence,
    getActivityEvidence,
    deleteActivityEvidenceFile,
    getActivitiesByPlanId,
    getRequirements,
    getExternalData,
    getAllChecklists,
    fetchChecklistsByVersionId,
    getChecklistResponses,
    saveChecklistResponse,
    saveChecklistResponsesBatch,
    uploadChecklistAnswerEvidence,
    resolveChecklistAnswerEvidence,
    getPlanValidations,
    saveCEOValidation,
    exportAnnualPlansToCSV,
    exportAnnualPlansToExcel,
    getCookieReport,
    addFocusDefinitionApproval,
    addPlanDefinitionApproval,
    getFocusDefinitionApprovals,
    getPlanDefinitionApproval,
    createFulfillmentVersion,
    markPlanVersionForApproval,
    markFulfillmentVersionForApproval,
    reopenRejectedPlanVersion,
    reopenRejectedFulfillmentVersion,
    addFocusFulfillmentValidation,
    addPlanValidation,
    getPlanValidation,
    uploadPlanValidationSignature,
    getFocusFulfillmentValidations,
    deleteAnnualPlan,
    updateAnnualPlan,
    getAnnualPlanStep0Context,
    getAnnualPlanStep1Context,
    getAnnualPlanStep2Context,
    getAnnualPlanComplianceContext,
    getPlanRequirementDecisions,
    savePlanRequirementDecisions,
    anularPlanVersion,
    returnPlanToDefinition,
    getCurrentUserRoles,
    getOutOfTimePlanRequestStatus,
    createOutOfTimePlanRequest,
    decideOutOfTimePlanRequest,
    decideReopenDefinitionDecision,
    decideReopenFulfillmentDecision,
    getAccidentAnalysis,
    getSelfAssessmentResults,
    getSelfAssessmentPowerBiReportImage,
  };

  async function getPaginatedPlans(
    params: PaginationParams,
    filters?: {
      hierarchicalModelId?: string;
      status?: string;
      year?: string;
      fromDate?: string;
      toDate?: string;
      skipDefaultYear?: string;
    },
  ): Promise<PaginatedPlansResponse> {
    const queryParams = new URLSearchParams({
      page: params.page.toString(),
      limit: params.limit.toString(),
      sortBy: params.sortBy || DEFAULT_SORT_BY,
      sortOrder: params.sortOrder || 'desc',
    });

    if (filters) {
      if (filters.hierarchicalModelId) {
        queryParams.append('hierarchicalModelId', filters.hierarchicalModelId);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.year) {
        queryParams.append('year', filters.year);
      }
      if (filters.fromDate) {
        queryParams.append('fromDate', filters.fromDate);
      }
      if (filters.toDate) {
        queryParams.append('toDate', filters.toDate);
      }
      if (filters.skipDefaultYear) {
        queryParams.append('skipDefaultYear', filters.skipDefaultYear);
      }
    }

    const response = await apiRequest('GET', `${baseUrl}/${PATH_PLANS_PAGINATED}?${queryParams}`);
    return response.json();
  }

  async function getAnnualPlanById(id: number): Promise<any> {
    const response = await apiRequest('GET', `${baseUrl}/${PATH_V2_PLANS}/${id}`);
    return response.json();
  }

  async function deleteAnnualPlan(id: number): Promise<void> {
    await apiRequest('DELETE', `${baseUrl}/${PATH_PLANS}/${id}`);
  }

  async function createFulfillmentVersion(versionId: number): Promise<any> {
    const response = await apiRequest(
      'POST',
      `${baseUrl}/api/annual-plans/${versionId}/fulfillment-versions`,
    );
    return response.json();
  }

  async function createAnnualPlan(data: CreatePlanInput): Promise<{ plan: any; version: any }> {
    const response = await apiRequest('POST', `${baseUrl}/${PATH_PLANS}`, data);
    return response.json();
  }

  async function updateAnnualPlan(
    planId: number,
    data: { Year?: number; HierarchicalModelId?: number },
  ): Promise<any> {
    const response = await apiRequest('PUT', `${baseUrl}/${PATH_PLANS}/${planId}`, data);
    return response.json();
  }

  async function getAnnualPlanStep0Context(params: {
    planId?: number;
    versionId?: number;
    year?: number;
  }): Promise<AnnualPlanStep0Context> {
    const query = new URLSearchParams();
    if (Number.isInteger(params.planId)) query.append('planId', String(params.planId));
    if (Number.isInteger(params.versionId)) query.append('versionId', String(params.versionId));
    if (Number.isInteger(params.year)) query.append('year', String(params.year));
    const response = await apiRequest(
      'GET',
      `${baseUrl}/${PATH_PLANS}/step0-context?${query.toString()}`,
    );
    return response.json();
  }

  async function getAnnualPlanStep1Context(versionId: number): Promise<AnnualPlanStep1Context> {
    const response = await apiRequest('GET', `${baseUrl}/${PATH_PLANS}/${versionId}/step1-context`);
    return response.json();
  }

  async function getAnnualPlanStep2Context(
    versionId: number,
    params?: { planId?: number; year?: number },
  ): Promise<AnnualPlanStep2Context> {
    const query = new URLSearchParams();
    const planId = params?.planId;
    const year = params?.year;
    if (Number.isInteger(planId)) query.append('planId', String(planId));
    if (Number.isInteger(year)) query.append('year', String(year));
    const suffix = query.toString();
    const response = await apiRequest(
      'GET',
      `${baseUrl}/${PATH_PLANS}/${versionId}/step2-context${suffix ? `?${suffix}` : ''}`,
    );
    return response.json();
  }

  async function getAnnualPlanComplianceContext(
    fulfillmentVersionId: number,
    params?: { versionId?: number },
  ): Promise<AnnualPlanComplianceContext> {
    const query = new URLSearchParams();
    const versionId = params?.versionId;
    if (Number.isInteger(versionId)) query.append('versionId', String(versionId));
    const suffix = query.toString();
    const response = await apiRequest(
      'GET',
      `${baseUrl}/${PATH_PLANS}/fulfillment-versions/${fulfillmentVersionId}/compliance-context${
        suffix ? `?${suffix}` : ''
      }`,
    );
    return response.json();
  }

  async function getPlanRequirementDecisions(planId: number): Promise<PlanRequirementDecision[]> {
    const response = await apiRequest(
      'GET',
      `${baseUrl}/api/annual-plans/${planId}/requirement-decisions`,
    );
    return response.json();
  }

  async function savePlanRequirementDecisions(
    planId: number,
    decisions: PlanRequirementDecisionInput[],
  ): Promise<void> {
    await apiRequest('POST', `${baseUrl}/api/annual-plans/${planId}/requirement-decisions`, {
      decisions,
    });
  }

  async function getCurrentUserRoles(): Promise<string[]> {
    const response = await apiRequest('GET', `${baseUrl}/api/me/roles`);
    const data = await response.json();
    return Array.isArray(data?.roles) ? data.roles : [];
  }

  async function getOutOfTimePlanRequestStatus(): Promise<{
    pendingRequest: unknown;
    approvedRequest: unknown;
  }> {
    const response = await apiRequest(
      'GET',
      `${baseUrl}/api/annual-plans/out-of-time-requests/status`,
    );
    return response.json();
  }

  async function createOutOfTimePlanRequest(reason: string): Promise<any> {
    const response = await apiRequest('POST', `${baseUrl}/api/annual-plans/out-of-time-requests`, {
      reason,
    });
    return response.json();
  }

  async function decideOutOfTimePlanRequest(
    requestId: number,
    decision: 'accept' | 'reject',
    token: string,
    rejectionReason?: string,
  ): Promise<any> {
    const response = await apiRequest(
      'POST',
      `${baseUrl}/api/annual-plans/out-of-time-requests/${requestId}/decision`,
      { decision, token, rejectionReason },
    );
    return response.json();
  }

  async function decideReopenDefinitionDecision(
    versionId: number,
    decision: 'accept' | 'reject',
    token: string,
  ): Promise<any> {
    const response = await apiRequest(
      'POST',
      `${baseUrl}/api/annual-plans/versions/${versionId}/reopen/decision`,
      { decision, token },
    );
    return response.json();
  }

  async function decideReopenFulfillmentDecision(
    fulfillmentVersionId: number,
    decision: 'accept' | 'reject',
    token: string,
  ): Promise<any> {
    const response = await apiRequest(
      'POST',
      `${baseUrl}/api/annual-plans/fulfillment-versions/${fulfillmentVersionId}/reopen/decision`,
      { decision, token },
    );
    return response.json();
  }

  async function markPlanVersionForApproval(versionId: number): Promise<any> {
    const response = await apiRequest(
      'POST',
      `${baseUrl}/api/annual-plans/${versionId}/mark-for-approval`,
    );
    return response.json();
  }

  async function markFulfillmentVersionForApproval(fulfillmentVersionId: number): Promise<any> {
    const response = await apiRequest(
      'POST',
      `${baseUrl}/api/annual-plans/fulfillment-versions/${fulfillmentVersionId}/mark-for-approval`,
    );
    return response.json();
  }

  async function reopenRejectedPlanVersion(versionId: number, reason: string): Promise<any> {
    const body = reason ? { ReopenReason: reason } : undefined;
    const response = await apiRequest(
      'POST',
      `${baseUrl}/api/annual-plans/versions/${versionId}/reopen`,
      body,
    );
    return response.json();
  }

  async function reopenRejectedFulfillmentVersion(
    fulfillmentVersionId: number,
    reason: string,
  ): Promise<any> {
    const body = reason ? { ReopenReason: reason } : undefined;
    const response = await apiRequest(
      'POST',
      `${baseUrl}/api/annual-plans/fulfillment-versions/${fulfillmentVersionId}/reopen`,
      body,
    );
    return response.json();
  }

  async function getAPVersions(annualPlanId: number): Promise<any> {
    const response = await apiRequest(
      'GET',
      `${baseUrl}/${PATH_V2_PLANS}/${annualPlanId}/versions`,
    );
    return response.json();
  }

  async function createAnnualPlanVersion(data: any): Promise<any> {
    const response = await apiRequest('POST', `${baseUrl}/${PATH_V2_VERSIONS}`, data);
    return response.json();
  }

  async function anularPlanVersion(apId: number): Promise<void> {
    await apiRequest('DELETE', `${baseUrl}/${PATH_PLANS}/${apId}/anular`);
  }

  async function returnPlanToDefinition(apId: number): Promise<void> {
    await apiRequest('POST', `${baseUrl}/${PATH_PLANS}/${apId}/return-to-definition`);
  }

  async function fetchAllFocuses(versionId: number): Promise<any> {
    const response = await apiRequest('GET', `${baseUrl}/${PATH_FOCUSES}/${versionId}`);
    return response.json();
  }

  async function getFocusNamesByIds(
    focusIds: number[],
    params?: { planId?: number; year?: number },
  ): Promise<FocusName[]> {
    if (!focusIds || focusIds.length === 0) return [];
    const uniqueIds = Array.from(new Set(focusIds));
    const query = new URLSearchParams();
    query.append('ids', uniqueIds.join(','));
    const planId = params?.planId;
    const year = params?.year;
    if (Number.isInteger(planId)) {
      query.append('planId', String(planId));
    } else if (Number.isInteger(year)) {
      query.append('year', String(year));
    }
    const response = await apiRequest('GET', `${baseUrl}/${PATH_FOCUSES}?${query.toString()}`);
    return response.json();
  }

  async function fetchFocusesFulfillmentProgress(fulfillmentVersionId: number): Promise<any> {
    const response = await apiRequest(
      'GET',
      `${baseUrl}/${PATH_FULFILLMENT_PROGRESS}/${fulfillmentVersionId}/focuses`,
    );
    return response.json();
  }

  async function fetchObjectivesByVersionId(versionId: number): Promise<any> {
    const response = await apiRequest('GET', `${baseUrl}/api/annual-plans/${versionId}/objectives`);
    return response.json();
  }

  async function saveObjectivesBatch(
    versionId: number,
    data: ObjectiveBatchSaveInput,
  ): Promise<ObjectiveBatchSaveResult> {
    const response = await apiRequest(
      'POST',
      `${baseUrl}/${PATH_PLANS}/${versionId}/objectives/batch`,
      data,
    );
    return response.json();
  }

  async function createObjective(data: CreateObjectiveInput): Promise<Objective> {
    const response = await apiRequest('POST', `${baseUrl}/${PATH_OBJECTIVES}`, data);
    return response.json();
  }

  async function updateObjective(
    objectiveId: number,
    data: UpdateObjectiveInput,
  ): Promise<Objective> {
    const response = await apiRequest('PUT', `${baseUrl}/${PATH_OBJECTIVES}/${objectiveId}`, data);
    return response.json();
  }

  async function deleteObjective(objectiveId: number): Promise<void> {
    await apiRequest('DELETE', `${baseUrl}/${PATH_OBJECTIVES}/${objectiveId}`);
  }

  async function getActivitiesByObjectiveId(
    objectiveId: number,
    fulfillmentversion?: number,
  ): Promise<Activity[]> {
    let url = `${baseUrl.replace(/\/$/, '')}/api/objectives/${objectiveId}/activities`;
    if (fulfillmentversion !== undefined && fulfillmentversion !== null) {
      const qp = `fulfillmentversion=${encodeURIComponent(String(fulfillmentversion))}`;
      url += (url.includes('?') ? '&' : '?') + qp;
    }
    const response = await apiRequest('GET', url);
    return response.json();
  }

  async function createActivity(data: CreateActivityInput): Promise<Activity[]> {
    const response = await apiRequest('POST', `${baseUrl}/${PATH_ACTIVITIES}`, data);
    return response.json();
  }

  async function saveActivitiesBatch(
    versionId: number,
    data: ActivityBatchSaveInput,
  ): Promise<ActivityBatchSaveResult> {
    const response = await apiRequest(
      'POST',
      `${baseUrl}/${PATH_PLANS}/${versionId}/activities/batch`,
      data,
    );
    return response.json();
  }

  async function updateActivityDefinition(
    activityId: number,
    data: Partial<CreateActivityInput>,
  ): Promise<Activity> {
    const response = await apiRequest('PUT', `${baseUrl}/api/activities/${activityId}`, data);
    return response.json();
  }

  async function deleteActivity(activityId: number): Promise<void> {
    await apiRequest('DELETE', `${baseUrl}/api/activities/${activityId}`);
  }

  async function addFocusDefinitionApproval(
    versionId: number,
    approvalData: CreateFocusDefinitionApprovalInput,
  ): Promise<any> {
    const response = await apiRequest(
      'POST',
      `${baseUrl}/api/annual-plans/${versionId}/focus-definition-approvals`,
      approvalData,
    );
    return response.json();
  }

  async function addPlanDefinitionApproval(
    versionId: number,
    approvalData: CreatePlanDefinitionApprovalInput,
  ): Promise<any> {
    const response = await apiRequest(
      'POST',
      `${baseUrl}/api/annual-plans/${versionId}/plan-definition-approvals`,
      approvalData,
    );
    return response.json();
  }

  async function getFocusDefinitionApprovals(versionId: number): Promise<any> {
    const response = await apiRequest(
      'GET',
      `${baseUrl}/api/annual-plans/${versionId}/focus-definition-approvals`,
    );
    return response.json();
  }

  async function getPlanDefinitionApproval(versionId: number): Promise<any> {
    const response = await apiRequest(
      'GET',
      `${baseUrl}/api/annual-plans/${versionId}/plan-definition-approval`,
    );
    return response.json();
  }

  interface UpdateActivityProgressInput {
    activityId: number;
    progress: number;
  }

  async function updateActivityProgress(
    input: UpdateActivityProgressInput[],
    fulfillmentId: number,
  ): Promise<void> {
    await apiRequest(
      'PUT',
      `${baseUrl}/api/activities-progress?fulfillmentId=${fulfillmentId}`,
      input,
    );
  }

  async function uploadActivityEvidence(
    activityId: number,
    files: File[],
    fulfillmentId: number,
  ): Promise<string[]> {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    const response = await fetch(
      appendScopeToApiUrl(
        `${baseUrl}/api/activities/${activityId}/evidence?fulfillmentId=${fulfillmentId}`,
      ),
      {
        method: 'POST',
        body: formData,
        credentials: 'include',
      },
    );
    if (!response.ok) throw new Error('Failed to upload evidence');
    const json = await response.json();
    if (Array.isArray(json.files)) {
      return json.files.map((f: any) => (typeof f === 'string' ? f : f.name)).filter(Boolean);
    }
    if (Array.isArray(json.names)) return json.names;
    return [];
  }

  async function deleteActivityEvidence(activityId: number, fulfillmentId: number): Promise<void> {
    const response = await apiRequest(
      'DELETE',
      `${baseUrl}/api/activities/${activityId}/evidence?fulfillmentId=${fulfillmentId}`,
    );
    if (!response.ok && response.status !== 204) throw new Error('Failed to delete evidence');
  }

  async function deleteActivityEvidenceFile(
    activityId: number,
    fileName: string,
    fulfillmentId: number,
  ): Promise<void> {
    const response = await apiRequest(
      'DELETE',
      `${baseUrl}/api/activities/${activityId}/evidence/${encodeURIComponent(fileName)}?fulfillmentId=${fulfillmentId}`,
    );
    if (!response.ok && response.status !== 204) throw new Error('Failed to delete evidence file');
  }

  async function getActivityEvidence(
    activityId: number,
    fulfillmentId: number,
  ): Promise<{ name: string; url: string }[]> {
    const response = await apiRequest(
      'GET',
      `${baseUrl}/api/activities/${activityId}/evidence?fulfillmentId=${fulfillmentId}`,
    );
    if (!response.ok) throw new Error('Failed to fetch evidence');
    const json = await response.json();
    return json.files || [];
  }

  async function getActivitiesByPlanId(
    versionId: number,
    fulfillmentversion?: number,
  ): Promise<Activity[]> {
    try {
      let url = `${baseUrl.replace(/\/$/, '')}/${PATH_PLANS}/${versionId}/activities`;
      if (fulfillmentversion !== undefined && fulfillmentversion !== null) {
        const qp = `fulfillmentversion=${encodeURIComponent(String(fulfillmentversion))}`;
        url += (url.includes('?') ? '&' : '?') + qp;
      }
      const response = await apiRequest('GET', url);
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('Invalid response type, expected JSON but got:', contentType);
        return [];
      }
      return response.json();
    } catch (error) {
      console.error('Error fetching activities by plan ID:', error);
      return [];
    }
  }

  async function getRequirements(params?: {
    planId?: number;
    year?: number;
  }): Promise<FocusRequirementWithDetails[]> {
    if (!Number.isInteger(params?.planId) && !Number.isInteger(params?.year)) {
      return [];
    }
    const query = new URLSearchParams();
    const planId = params?.planId;
    const year = params?.year;
    if (Number.isInteger(planId)) {
      query.append('planId', String(planId));
    } else if (Number.isInteger(year)) {
      query.append('year', String(year));
    }
    const suffix = query.toString();
    const url = suffix
      ? `${baseUrl}/${PATH_REQUIREMENTS}?${suffix}`
      : `${baseUrl}/${PATH_REQUIREMENTS}`;
    const response = await apiRequest('GET', url);
    return response.json();
  }

  async function getExternalData(): Promise<any> {
    const response = await apiRequest('GET', `${baseUrl}/${PATH_EXTERNAL}`);
    return response.json();
  }

  async function getAllChecklists(): Promise<any[]> {
    const response = await apiRequest('GET', `${baseUrl}/api/v2/checklists`);
    return response.json();
  }

  async function fetchChecklistsByVersionId(versionId: number): Promise<any[]> {
    const response = await apiRequest(
      'GET',
      `${baseUrl}/${PATH_CHECKLISTS}?versionId=${versionId}`,
    );
    return response.json();
  }

  async function getChecklistResponses(versionId: number): Promise<any[]> {
    const response = await apiRequest(
      'GET',
      `${baseUrl}/api/checklist-responses?versionId=${versionId}`,
    );
    return response.json();
  }

  async function saveChecklistResponse(
    data: SaveChecklistResponseDTO,
  ): Promise<SaveChecklistResponseResult> {
    const response = await apiRequest('POST', `${baseUrl}/api/checklist-responses`, data);
    return response.json();
  }

  async function saveChecklistResponsesBatch(
    responses: ChecklistResponseBatchInput[],
  ): Promise<ChecklistResponsesBatchSaveResult> {
    const response = await apiRequest('POST', `${baseUrl}/api/checklist-responses/batch`, {
      responses,
    });
    return response.json();
  }

  async function uploadChecklistAnswerEvidence(
    file: File,
  ): Promise<{ name: string; url: string; size?: number }> {
    const form = new FormData();
    form.append('file', file);
    const response = await fetch(appendScopeToApiUrl(`${baseUrl}/api/checklist-answers/evidence`), {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!response.ok) throw new Error('Error subiendo evidencia');
    return response.json();
  }

  async function resolveChecklistAnswerEvidence(
    name: string,
  ): Promise<{ name: string; url: string } | null> {
    if (!name) return null;
    if (/^https?:\/\//i.test(name)) return { name, url: name };
    try {
      const response = await apiRequest(
        'GET',
        `${baseUrl}/api/checklist-answers/evidence/${encodeURIComponent(name)}`,
      );
      if (!response.ok) return { name, url: name };
      return response.json();
    } catch (e) {
      console.warn('No se pudo resolver URL evidencia', e);
      return { name, url: name };
    }
  }

  async function getPlanValidations(planId: number): Promise<any[]> {
    const response = await apiRequest('GET', `${baseUrl}/${PATH_V2_PLANS}/${planId}/validations`);
    return response.json();
  }

  async function saveCEOValidation(data: any): Promise<any> {
    const response = await apiRequest('POST', `${baseUrl}/api/v2/validations`, data);
    return response.json();
  }

  async function exportAnnualPlansToCSV(filters?: AnnualPlanExportFilters): Promise<Blob> {
    const query = buildAnnualPlansExportQuery('csv', undefined, filters);
    const response = await apiRequest('GET', `${baseUrl}/${PATH_ANNUAL_PLANS_EXPORT}?${query}`);
    if (!response.ok) {
      throw new Error('Failed to export annual plans to CSV');
    }
    return response.blob();
  }

  async function exportAnnualPlansToExcel(
    scopeName?: string,
    filters?: AnnualPlanExportFilters,
  ): Promise<Blob> {
    const query = buildAnnualPlansExportQuery('excel', scopeName, filters);
    const response = await apiRequest('GET', `${baseUrl}/${PATH_ANNUAL_PLANS_EXPORT}?${query}`);
    if (!response.ok) {
      throw new Error('Failed to export annual plans to Excel');
    }
    return response.blob();
  }

  async function getCookieReport(): Promise<any> {
    const response = await apiRequest('GET', `${baseUrl}/${PATH_COOKIE_REPORT}`);
    return response.json();
  }

  async function addFocusFulfillmentValidation(
    fulfillmentVersionId: number,
    validationData: CreateFocusFulfillmentInput,
  ): Promise<any> {
    const response = await apiRequest(
      'POST',
      `${baseUrl}/api/annual-plans/fulfillment-versions/${fulfillmentVersionId}/focus-fulfillment-validations`,
      validationData,
    );
    return response.json();
  }

  async function addPlanValidation(
    fulfillmentVersionId: number,
    validationData: CreatePlanValidationInput,
  ): Promise<any> {
    const response = await apiRequest(
      'POST',
      `${baseUrl}/api/annual-plans/fulfillment-versions/${fulfillmentVersionId}/plan-validations`,
      validationData,
    );
    return response.json();
  }

  async function getPlanValidation(fulfillmentVersionId: number): Promise<any | null> {
    const response = await apiRequest(
      'GET',
      `${baseUrl}/api/annual-plans/fulfillment-versions/${fulfillmentVersionId}/plan-validation`,
    );
    if (response.status === 204) return null;
    return response.json();
  }

  async function uploadPlanValidationSignature(
    fulfillmentVersionId: number,
    dataUrl: string,
  ): Promise<{ name: string; url: string }> {
    const response = await apiRequest(
      'POST',
      `${baseUrl}/api/annual-plans/fulfillment-versions/${fulfillmentVersionId}/signature`,
      { dataUrl },
    );
    return response.json();
  }

  async function getFocusFulfillmentValidations(
    fulfillmentVersionId: number,
  ): Promise<{ APFocusFulfillmentId: number; APFocusId: number; Approved: boolean }[]> {
    const response = await apiRequest(
      'GET',
      `${baseUrl}/api/annual-plans/fulfillment-versions/${fulfillmentVersionId}/focus-fulfillment-validations`,
    );
    return response.json();
  }

  async function getSelfAssessmentResults(
    fulfillmentVersionId: number,
  ): Promise<SelfAssessmentResultsPayload> {
    const response = await apiRequest(
      'GET',
      `${baseUrl}/${PATH_SELF_ASSESSMENT_RESULTS}/${fulfillmentVersionId}/results`,
    );
    return response.json();
  }

  async function getSelfAssessmentPowerBiReportImage(
    fulfillmentVersionId: number,
  ): Promise<PowerBiReportImageResponse> {
    const response = await apiRequest(
      'GET',
      `${baseUrl}/${PATH_SELF_ASSESSMENT_RESULTS}/${fulfillmentVersionId}/powerbi-report`,
    );
    if (response.status === 204) {
      return { dataUrl: null, node0DataUrl: null };
    }
    return response.json();
  }

  async function getAccidentAnalysis(year?: number, month?: number) {
    const params = new URLSearchParams();
    if (Number.isInteger(year)) params.set('year', String(year));
    if (Number.isInteger(month)) params.set('month', String(month));
    const query = params.toString();
    const response = await apiRequest(
      'GET',
      `${baseUrl}/${PATH_ACCIDENT_ANALYSIS}${query ? `?${query}` : ''}`,
    );
    return response.json();
  }
}
